import React from 'react';
import { Avatar, AvatarProps, AvatarSize, Icon, IconName } from '@televet/kibble-ui';
import { SpeciesType } from 'shared/types/graphql';
import {
  CareSubscriptionStatus,
  getCareBadgeBackgroundColor,
  getCareBadgeIconVariantColor,
} from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/utils/careEnrollments';
import { getSpeciesIcon } from './utils';

interface SharedAvatarProps extends AvatarProps {
  speciesType?: SpeciesType;
  size?: AvatarSize;
  className?: string;
  color?: string;
  hideRightMargin?: boolean;
  isDeceased: boolean | undefined;
}

interface GeneralAvatarProps extends SharedAvatarProps {
  iconOnly?: never;
  photoUrl?: string | null;
  isActive: boolean | undefined;
  name?: string;
  careStatus?: CareSubscriptionStatus | null;
}

// Icon only avatars do not support photos, badges, or tooltips so these fields should not be passed
interface IconAvatarProps extends SharedAvatarProps {
  iconOnly?: boolean;
  photoUrl?: never;
  isActive?: never;
  name?: never;
  careStatus?: never;
}

export type PetAvatarProps = IconAvatarProps | GeneralAvatarProps;

const PetAvatar = ({
  speciesType,
  size = 'md',
  className,
  photoUrl,
  color,
  isDeceased = false,
  isActive = true,
  iconOnly = false,
  hideRightMargin = false,
  name,
  careStatus,
  ...rest
}: PetAvatarProps): JSX.Element => {
  const speciesIconName: IconName = getSpeciesIcon(speciesType, isDeceased);

  if (iconOnly) {
    return <Icon name={speciesIconName} size={size} />;
  }

  const careBadgeProps: AvatarProps = {
    showBadge: true,
    badgeProps: {
      bg: getCareBadgeBackgroundColor(careStatus),
      iconVariant: getCareBadgeIconVariantColor(careStatus),
      iconName: 'starSolid',
      position: 'topRight',
    },
  };

  return (
    <Avatar
      name={isDeceased || !isActive ? `${name} (Inactive)` : name}
      hideBorder
      size={size}
      iconName={speciesIconName}
      data-testid="pet-avatar"
      {...(photoUrl &&
        !isDeceased && {
          src: photoUrl,
        })}
      color={color ?? 'text.default'}
      backgroundColor="background.lessSubtle"
      className={className}
      mr={hideRightMargin ? 0 : 2}
      showBadge={isDeceased || !isActive}
      badgeProps={{
        iconName: 'cancel',
        bg: 'background.danger',
        iconVariant: 'danger',
        position: 'topRight',
      }}
      {...(!!careStatus && !(isDeceased || !isActive) && careBadgeProps)}
      {...rest}
    />
  );
};

export default PetAvatar;
